/**
 * examPaperApi — 신규 시험지(⑦) + 폴더 + 배포·채점 + AI 복사 배치 + 학생 조회.
 *
 * 계약 정본: docs/exam-builder-v2/BE-20260826-audit-fixes-folder-tree-v2apis.md
 *       (감사 수정 38건 · 폴더 계층 · v2 학생/개념 API — 2026-08-26 dev 배포·e2e 39/39).
 * 함께 읽는 것: BE-20260825-exam-papers-screens.md · BE-20260825-authoring-assign-testcreate.md(출제·시험정보·배정) ·
 *       BE-20260825-contract-answers.md(응답 형태 확정) · BE-20260825-folders.md(폴더 1세대 — 계층은 정본 §2 가 덮어쓴다).
 * 봉투 { status, message, status_code, data } — 성공 판정 status === 'success'.
 *
 * 두 베이스가 섞인다. 시험지 자체는 /api/v2/exam-papers, AI 복사 배치는 변형 데몬 소유라
 * /api/v2/problem-variation 밑이다(계약 §4 헤더). 멤버 검수는 단건 변형과 완전히 같은
 * 엔드포인트(GET /variations/{vid})라 검수 카드를 그대로 재사용한다.
 *
 * 학생 조회도 v2 다 — 2026-08-26 오너 방침(전부 v2)으로 GET /api/v2/students 하나로 갈아탔다(계약 §7).
 * 계정 종류로 경로가 갈리던 v1(/api/users/student 대 /student/allocate + teacher_id)은 여기서 더 이상 쓰지 않는다.
 * **범위를 토큰이 정한다** — 매니저·관리자는 전체, 교사는 자기 담당. 화면이 userType 을 보고 고를 것이 없어졌다.
 */
import { fetchWithTokenRefresh, fetchAPI, AUTH_REDIRECT } from '../../utils/apiUtils';
import { formatV2Timestamp } from '../../utils/v2DateTime';

// 401 표식은 공용 자리(utils/apiUtils)에 하나만 둔다 — 여기서는 그대로 다시 내보낸다
export { AUTH_REDIRECT };

const EP = '/api/v2/exam-papers';
const VAR = '/api/v2/problem-variation';
const STU = '/api/v2/students';

function unwrap(res, fallbackMsg) {
  if (!res || res.status !== 'success') {
    throw new Error(res?.message || fallbackMsg);
  }
  return res.data;
}

/**
 * 목록 = data.items (계약 §1-1·§1-2). 형태가 확정됐어도 값은 비어 올 수 있어,
 * 배열이 아니면 빈 배열을 돌려준다 — 화면이 목록 자리에서 터지지 않게.
 */
function toItems(d) {
  if (Array.isArray(d)) return d;
  return Array.isArray(d?.items) ? d.items : [];
}

function pageMeta(d, page, perPage, items) {
  return {
    page: d?.page ?? page,
    perPage: d?.per_page ?? perPage,
    totalItems: d?.total_items ?? items.length,
    totalPages: d?.total_pages ?? 1,
  };
}

/**
 * 쓰기 호출 — 공용 fetchWithTokenRefresh 는 !ok 일 때 봉투를 읽지 않고 자체 문구로 갈아 치운다.
 * 그러면 400 의 검증 사유가 사라지고, 조립 게이트의 409 도 문자열로만 남는다. 이 계열은
 * 봉투의 message·HTTP 상태·data 를 그대로 실어 나른다(variationApi 관례).
 * data 까지 싣는 이유: 409 는 data.reason 으로 갈래를 나눠야 한다(contract-answers §2-10, 정본 §1-3·§10).
 */
async function requestJsonWithStatus(path, { method = 'POST', payload } = {}, getAccessToken, fallbackMsg) {
  const token = await getAccessToken();
  if (!token) {
    window.location.href = '/signin';
    return AUTH_REDIRECT;
  }
  let response;
  try {
    response = await fetchAPI(path, {
      method,
      headers: { Authorization: token, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload ?? {}),
    });
  } catch (netErr) {
    throw new Error('서버에 연결할 수 없습니다. 인터넷 연결을 확인하거나 잠시 후 다시 시도해주세요.');
  }
  if (response.status === 401) {
    // 공용 유틸과 같은 뒷정리 — 죽은 토큰을 남겨 두면 다음 화면이 또 401 을 맞는다
    // (계약 §6: soft-delete 된 사용자의 유효 토큰이 이제 401 이라 이 갈래가 실제로 돌게 됐다).
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    window.location.href = '/signin';
    return AUTH_REDIRECT;
  }
  let body = null;
  try { body = await response.json(); } catch (_) { /* 상태코드로 표면화 */ }
  if (!response.ok || !body || body.status !== 'success') {
    const err = new Error(body?.message || `${fallbackMsg} (${response.status})`);
    err.httpStatus = response.status;
    err.data = body?.data ?? null;
    throw err;
  }
  return body.data;
}

// ── 오류 사유(403/409/423) ────────────────────────────────────────────────
//
// 서버는 막은 이유를 봉투 data.reason 에 기계 판별 코드로 준다. 문구는 언제든 바뀌므로
// **화면은 문자열이 아니라 reason 으로 분기한다**(정본 §1-3·§10, contract-answers §2-10).
//
// ⚠ reason 이 실려 오는 것은 **쓰기 계열(requestJsonWithStatus)뿐**이다. 읽기 계열이 쓰는 공용
// fetchWithTokenRefresh 는 !ok 일 때 자기 문구로 갈아 친 Error 만 던져 data 가 없다 — 읽기에서
// errorReason 은 항상 null 이다.
//
// 2026-09-10 로 「위 사유들은 전부 쓰기에서만 난다」가 깨졌다 — `out_of_scope` 는 **읽기에서도 난다**
// (교사 토큰이 담당 밖 학생·남의 폴더를 조회할 때. teacher-access §1 표·§2-2). 그런데도 이 함수에
// 닿지 않는 이유는 사유가 없어서가 아니라 **공용 fetchWithTokenRefresh 가 봉투를 통째로 버려서**다:
// 그 자리에서 403 은 사유를 읽기도 전에 apiUtils.js:76 의 「접근 권한이 없습니다.」로 갈아 치인다.
// 그래서 읽기 403 의 문구는 **여기를 고쳐서는 한 글자도 바뀌지 않는다** — 고치려면 저장소 43개
// 파일이 함께 쓰는 그 공용 함수를 고쳐야 하고, 그것은 이 범위가 아니다.

/** 403/409/423 의 기계 판별 코드. 읽기 오류·네트워크 오류면 null */
export function errorReason(err) {
  return err?.data?.reason ?? null;
}

/** 전부 폐기된 배치의 시험지 생성 거절 — 사유 문구와 배치 화면이 같은 말을 한다 */
export const ALL_REJECTED_TEXT = '모든 문항이 폐기돼 시험지를 만들 수 없습니다 — 필요 없으면 [시험지 폐기]로 정리하고, 다시 만들려면 원본 시험지에서 AI 복사를 다시 거세요.';

/** 목록 앞 몇 개만 '#12, #34 외' 로 — 사유 문구에 문항 번호를 실어 어느 것인지 짚어 준다 */
function briefIds(ids, head = 5) {
  const list = Array.isArray(ids) ? ids.filter((v) => v != null) : [];
  if (list.length === 0) return '';
  const shown = list.slice(0, head).map((v) => `#${v}`).join(', ');
  return list.length > head ? `${shown} 외` : shown;
}

/**
 * 오류 → 화면에 그대로 띄울 한국어 문장 하나(계약 §1-2·§1-3·§2-10·§3·§4·§10).
 * 사유를 아는 것만 우리 문구로 갈아 끼우고, 모르는 것은 서버 message → fallbackText 순으로 흘린다 —
 * 사유 표가 늘어도 화면이 빈 문자열을 띄우지 않게.
 */
export function describeApiError(err, fallbackText = '요청을 처리하지 못했습니다.') {
  const reason = errorReason(err);
  const d = err?.data ?? null;
  switch (reason) {
    case 'deleted_items': {
      // §1-3 — 배포·복사·되돌리기 공통. 삭제된 문항은 스냅샷을 뜰 수 없다
      const n = Array.isArray(d?.problem_ids) ? d.problem_ids.length : 0;
      const which = briefIds(d?.problem_ids);
      return n > 0
        ? `삭제된 문항 ${n}개(${which})가 들어 있습니다 — 그 문항을 빼고 다시 시도하세요.`
        : '삭제된 문항이 들어 있습니다 — 그 문항을 빼고 다시 시도하세요.';
    }
    case 'missing_difficulty': {
      // §1-3 — 난이도(ProblemDifficulty)가 없으면 배포 뒤 리포트가 서지 않아 배포 자체를 막는다
      const n = Array.isArray(d?.problem_ids) ? d.problem_ids.length : 0;
      const which = briefIds(d?.problem_ids);
      return n > 0
        ? `난이도가 없는 문항 ${n}개(${which})가 있어 배포할 수 없습니다 — 문제 메뉴에서 분석을 먼저 끝내세요.`
        : '난이도가 없는 문항이 있어 배포할 수 없습니다 — 문제 메뉴에서 분석을 먼저 끝내세요.';
    }
    case 'has_assignments': {
      // §10 — 배정된 시험지는 삭제 불가(오너 결정 2026-08-26). 배정 단건 취소 API 는 아직 없다
      const n = Number(d?.assignment_count);
      return Number.isFinite(n) && n > 0
        ? `이미 ${n}건 배포된 시험지라 삭제할 수 없습니다 — 채점 완료된 배포본까지 그대로 남습니다.`
        : '이미 배포된 시험지라 삭제할 수 없습니다 — 채점 완료된 배포본까지 그대로 남습니다.';
    }
    case 'closed':
      // 배정 삭제(2026-09-06) — 마감분은 리포트에 편입된 상태라 재오픈을 먼저 거치게 한다
      return '채점이 완료된 배정이라 삭제할 수 없습니다 — [채점 수정]으로 마감을 먼저 풀어 주세요.';
    case 'scheduled':
      // §1-2 — open_at 이 아직 오지 않은 예약 배정. 채점·마감 둘 다 막힌다
      return '아직 오픈 전(예약)인 배정이라 채점하거나 완료할 수 없습니다 — 오픈 시각이 지나야 열립니다.';
    case 'stale':
      // §3 — 다른 곳(분석 승인·본문 되돌리기·다른 검수자)이 먼저 updated_at 을 밀었다
      return '다른 곳에서 먼저 수정했습니다 — 최신 상태를 다시 읽은 뒤 저장하세요.';
    case 'unsettled_members': {
      // §2-10 — 조립 게이트. 승인(200)/폐기(400) 로 정리된 멤버만 통과한다
      const ids = Array.isArray(d?.variation_ids) ? d.variation_ids : [];
      return ids.length > 0
        ? `미검수 ${ids.length}문항(${briefIds(ids)})이 남아 조립하지 못했습니다 — 승인하거나 폐기하면 열립니다.`
        : '아직 정리되지 않은 문항이 있어 조립하지 못했습니다 — 승인하거나 폐기하면 열립니다.';
    }
    case 'all_rejected':
      // 폐기한 문항은 되살리는 길이 없다(BE-20261102 회신 §2) — 「다시 생성해 승인」은 안내하면 거짓이 된다
      return ALL_REJECTED_TEXT;
    case 'empty_batch':
      return '이 배치에는 문항이 없습니다 — 원본 시험지에 문항이 없었던 것으로 보입니다.';
    case 'already_assembled':
      // 배치 폐기(BE-20261102 회신 §1) — 다른 창에서 먼저 시험지를 만들었다
      return '이미 시험지를 만들어 폐기할 수 없습니다 — 최신 상태를 다시 읽습니다.';
    case 'batch_discarded':
      return '폐기해서 시험지를 만들 수 없습니다 — 다시 만들려면 원본 시험지에서 AI 복사를 다시 거세요.';
    case 'busy':
      // 배치 폐기 — 문항 행이 잠겨 5초 안에 못 끝냈다(백엔드 289ec24, 회신 문서에는 없고 코드에만 있다)
      return '생성을 마무리하는 문항이 있어 지금은 폐기할 수 없습니다 — 잠시 뒤 다시 시도하세요.';
    case 'image_placeholder':
      // §4 D4 — 본문에 [그림] 자리표시자가 남아 있으면 승인이 막힌다
      return '본문에 [그림] 자리표시자가 남아 있어 승인할 수 없습니다 — image 블록을 지우거나 그림 spec 을 고친 뒤 다시 시도하세요.';
    case 'nothing_to_apply':
      // §3 — 분석 승인인데 반영할 변경이 없다(이미 같은 값이거나 검수 편집이 비었다)
      return '반영할 변경이 없습니다.';
    case 'out_of_scope': {
      // 2026-09-10 teacher-access §1 표·§2-2 — 교사 토큰이 **담당·공유 범위 밖**을 건드렸다.
      // 이 사유만 **403** 이다(위 사유들은 409/423). 부가 정보가 세 갈래로 갈린다:
      // 배정 `student_ids` · 시험지 이동 `paper_ids` · 폴더 `folder_id`.
      //
      // **좁은 것부터 본다.** 사람이 지금 손댈 수 있는 쪽(명단·고른 시험지)을 먼저 짚어야 다음 손이
      // 정해진다. 폴더는 그 사람이 손볼 수 있는 것이 아니라(만든 사람의 일) 맨 뒤다. 둘이 함께 와도
      // 이 차례가 문구를 정한다 — 「폴더가 보기 전용」이라고 먼저 말하면, 실은 학생 하나만 빼면
      // 되는 일에 사용자가 폴더 주인을 찾아 나선다.
      //
      // ⚠ **어느 갈래도 빈 문자열을 돌려주지 않는다.** 이 함수를 `describeApiError(e, '')` 로 부르는
      // 자리가 있다(StudentsHub/HubTestReportTabs.jsx:349 — 앞말 「시험지는 저장됐지만 출제하지
      // 못했습니다.」를 화면이 이미 붙여 놓고 사유만 이어 붙인다). 거기서 빈 값을 돌리면 사유가
      // 통째로 사라져, 무엇을 빼야 열리는지가 화면 어디에도 남지 않는다.
      //
      // 공유 폴더 어휘는 folderOwnership.js 의 SHARED_LOCK·PAPER_LOCK 과 **같은 말**을 쓴다
      // (「공유 폴더」·「볼 수만 있습니다」) — 같은 사실을 두 화면이 다른 말로 하면 사용자는 서로
      // 다른 두 제약으로 읽는다. 다만 그 상수를 import 하지는 **않는다**: 서버가 준 사유의 문구는
      // examPaperApi 가, 화면이 미리 거는 잠금의 문구는 folderOwnership 이 갖는 것이 기존 분담이고,
      // 여기서 끌어오면 API 모듈이 화면 모듈에 매달린다.
      const students = Array.isArray(d?.student_ids) ? d.student_ids.length : 0;
      if (students > 0) {
        return `담당이 아닌 학생 ${students}명(${briefIds(d?.student_ids)})이 있습니다 — 빼고 다시 시도하세요.`;
      }
      const papers = Array.isArray(d?.paper_ids) ? d.paper_ids.length : 0;
      if (papers > 0) {
        return `권한 밖 시험지 ${papers}개(${briefIds(d?.paper_ids)})가 있습니다 — 빼고 다시 시도하세요.`;
      }
      // folder_id 는 목록이 아니라 **스칼라**다. 그래도 briefIds 로 감싸는 이유는 표기를 한 자리에
      // 두기 위해서다 — `#` 접두와 null 걸러내기가 이미 그 함수에 있고, 여기서 직접 찍으면 같은
      // 표기가 두 벌이 된다(그리고 키가 null 로 오는 날 `#undefined` 가 화면에 뜬다).
      if (d?.folder_id != null) {
        return `보기 전용 공유 폴더(${briefIds([d.folder_id])})입니다 — 폴더 주인만 바꿀 수 있습니다.`;
      }
      return '담당·공유 범위 밖입니다 — 필요하면 관리자에게 공유를 요청하세요.';
    }
    case 'not_root': {
      // share-all-teachers §2-3 — 폴더 공유는 **최상위 폴더에만** 걸린다. 하위 폴더에 PUT 하면
      // 400 이고 `root_folder_id`(그 폴더가 속한 최상위)가 동봉된다.
      // 화면이 하위 폴더에서 [공유] 항목을 아예 감추므로 평소에는 나지 않는다 — 낡은 트리를 들고
      // 눌렀을 때의 **마지막 방어선**이라, 서버가 짚어 준 폴더를 그대로 가리켜 준다.
      const root = briefIds([d?.root_folder_id]);
      return root !== ''
        ? `최상위 폴더(${root})에서만 공유할 수 있습니다.`
        : '최상위 폴더에서만 공유할 수 있습니다.';
    }
    default:
      break;
  }
  // §1-2 — 재오픈은 마감된 배정에만 뜻이 있다. 미마감에 부르면 423 이 오고 reason 은 없다
  if (err?.httpStatus === 423) return '채점 완료된 배정만 다시 수정할 수 있습니다 — 이 배정은 아직 채점 완료되지 않았습니다.';
  return err?.message || fallbackText;
}

// ── 시각(시험 정보) ────────────────────────────────────────────────────────

const pad2 = (n) => String(n).padStart(2, '0');

/**
 * datetime-local 값 → 오프셋이 붙은 ISO. 서버는 시각을 UTC 로 정규화하므로 오프셋 없는
 * '2026-09-01T09:00:00' 은 UTC 로 읽힌다 — 한국시간이면 9시간 어긋난다(authoring §4-1).
 * 이미 오프셋이나 Z 가 붙은 값은 그대로 둔다(두 번 붙이면 그 자체로 어긋난다).
 * 예: toOffsetIso('2026-09-01T09:00') → '2026-09-01T09:00:00+09:00'
 */
export function toOffsetIso(local) {
  if (local == null) return null;
  const v = String(local).trim();
  if (v === '') return null;
  if (/(?:Z|[+-]\d{2}:?\d{2})$/.test(v)) return v;
  const withSec = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(v) ? `${v}:00` : v;
  // 오프셋 없는 date-time 은 로컬 시각으로 파싱된다 — 그 시점의 오프셋이라 서머타임도 맞는다
  const d = new Date(withSec);
  if (Number.isNaN(d.getTime())) return null;
  const mins = -d.getTimezoneOffset();
  const abs = Math.abs(mins);
  return `${withSec}${mins < 0 ? '-' : '+'}${pad2(Math.floor(abs / 60))}:${pad2(abs % 60)}`;
}

/** API 시각 → 한국 시각. 존 없는 옛 응답도 UTC로 읽는다. 예약 입력(datetime-local)은 toOffsetIso가 따로 맡는다. */
export const fmtServerDateTime = (s) => formatV2Timestamp(s);

/**
 * 시험 정보(전부 선택) → 요청 body. 비운 칸은 키 자체를 보내지 않는다 —
 * 빈 문자열은 "지운다"로 읽힐 여지가 있어, 안 쓴 것과 지우는 것이 뒤섞인다.
 */
function examInfoBody(examInfo) {
  const body = {};
  if (!examInfo) return body;
  const text = (v) => (typeof v === 'string' ? v.trim() : (v == null ? '' : String(v).trim()));
  const examType = text(examInfo.examType);
  if (examType !== '') body.exam_type = examType;
  const round = text(examInfo.examRound);
  if (round !== '' && Number.isFinite(Number(round))) body.exam_round = Math.trunc(Number(round));
  const examDate = text(examInfo.examDate);
  if (examDate !== '') body.exam_date = examDate;
  const openAt = toOffsetIso(examInfo.openAt);
  if (openAt) body.open_at = openAt;
  const closeAt = toOffsetIso(examInfo.closeAt);
  if (closeAt) body.close_at = closeAt;
  return body;
}

// ── 시험지 ────────────────────────────────────────────────────────────────

/**
 * 목록 정렬 허용값(계약 §1-4 · `assign_count` 는 BE-20260905-reply-paper-list-assign-count §1 2-2).
 * **그 밖의 값이면 서버가 400 이라** 화면 셀렉트의 선택지를 여기서 하나로 잡는다 —
 * 목록이 통째로 실패하는 것보다 정렬 없이 뜨는 편이 낫다.
 *
 * `assign_count`(출제 많은/적은 순)는 **배포된 서버에서만 받는다**. 아직 안 받는 서버에 보내면
 * 400 이라 목록이 통째로 실패한다 — 그 서버는 목록 항목에 `assign_count` 키도 싣지 않으므로
 * (회신 §1 2-3) 배지가 안 뜨는 것으로 먼저 드러난다.
 */
export const PAPER_SORTS = ['created_at', 'updated_at', 'title', 'assign_count'];
export const PAPER_ORDERS = ['asc', 'desc'];

/** folder_id 는 정수(그 폴더) 말고 문자열 'none'(무소속)도 온다 — include_children 은 정수일 때만 뜻이 있다 */
const isNumericFolderId = (v) => v != null && /^\d+$/.test(String(v).trim());

/**
 * 목록 — GET /papers?page&per_page&mine&folder_id&include_children&q&sort&order
 * folderId: 정수(그 폴더) · 'none'(무소속) · null/undefined(전체). 'none' 은 문자열 그대로 보낸다.
 * includeChildren: 그 폴더 **+ 모든 자손 폴더**의 시험지(계약 §2). folderId 가 정수일 때만 붙인다 —
 *   'none'(무소속)·전체에는 자손이라는 개념이 없어 보내 봐야 뜻이 없다.
 * q: 제목 부분검색(§1-4). `%`·`_` 는 서버가 리터럴로 다루니 프론트에서 이스케이프하지 않는다.
 * sort·order: 허용값 밖이면 **파라미터 자체를 안 보낸다**(서버 기본 created_at desc). 400 을 프론트에서 막는 자리다.
 * problemId: **그 문제가 담긴 시험지만**(problem-hub-v2/BE-20260901-reply-paper-usage-by-problem.md §1 2-1). 다른 필터와 AND.
 *   이때만 각 항목에 `matched_order_no`(0부터 — 상세 `items[].order_no` 와 같은 기준)·`matched_points`
 *   가 동봉된다. 안 줄 때의 응답은 종전과 같다(회신이 테스트로 고정).
 *
 * `assign_count` 는 **`problem_id` 유무와 무관하게 항상** 실린다(BE-20260905-reply §1 2-1, dev `85e67f7`).
 * 배정이 없으면 `0` 이고 키는 늘 있다 — 키가 아예 없으면 그 서버가 아직 안 받은 것이다(같은 회신 §1 2-3).
 *
 * 응답 항목에 작성자 3필드가 실린다 — `author_uuid` · `author_name` · `is_mine`
 * (BE-20260829-reply-fe-0828.md §3 2-1. 상세 GET /papers/{id} 도 같다).
 * `author_name` 은 사람 이름이고 비어 있으면 로그인 id, **삭제·부재 사용자면 `null`** 이다.
 * 여기서 이름을 바꿔 담지 않는다 — 같은 값에 이름이 둘이 되면 어느 쪽이 정본인지 다시 물어야 한다.
 * 그리는 자리는 PaperTable(paperAuthorName)·PaperListPanel 이다.
 */
export async function listPapers(
  {
    page = 1, perPage = 20, mine = false, folderId = null, includeChildren = false, q = '', sort = '', order = '',
    problemId = null,
  } = {},
  getAccessToken,
) {
  const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
  if (mine) params.append('mine', '1');
  if (folderId != null) params.append('folder_id', String(folderId));
  if (includeChildren && isNumericFolderId(folderId)) params.append('include_children', '1');
  const term = typeof q === 'string' ? q.trim() : (q == null ? '' : String(q).trim());
  if (term !== '') params.append('q', term);
  if (PAPER_SORTS.includes(sort)) params.append('sort', sort);
  if (PAPER_ORDERS.includes(order)) params.append('order', order);
  // 역조회 — 값을 손보지 않고 그대로 보낸다. 정수가 아니면 서버가 400 으로 말한다(조용히 무시하지 않는다,
  // 회신 §1). 여기서 걸러 **안 보내면** 전체 목록이 돌아와 「모든 시험지에 들어 있다」가 된다 — 그쪽이 더 나쁘다.
  if (problemId != null) params.append('problem_id', String(problemId));
  const res = await fetchWithTokenRefresh(`${EP}/papers?${params}`, {}, getAccessToken);
  const d = unwrap(res, '시험지 목록을 불러오지 못했습니다.') || {};
  const items = toItems(d);
  return { items, ...pageMeta(d, page, perPage, items) };
}

/**
 * 상세 — GET /papers/{id} → 현재 문항 + 라벨
 * 문항 배열 `items[]` 의 각 항목에 **`is_deleted:boolean`**, 최상위에 **`deleted_item_count:int`** 가 온다(계약 §1-3).
 * 삭제된 문항이 하나라도 있으면 배포·복사·되돌리기가 409 로 막히므로, 화면은 이 둘로
 * **배지와 버튼 비활성**을 미리 그린다 — 눌러 보고 나서 막히는 것보다 낫다. 판정은 아래 두 헬퍼로.
 * (생성·수정·복사 응답의 상세에도 같은 필드가 실린다.)
 */
export async function fetchPaper(paperId, getAccessToken) {
  const res = await fetchWithTokenRefresh(`${EP}/papers/${paperId}`, {}, getAccessToken);
  const d = unwrap(res, '시험지를 불러오지 못했습니다.');
  // 봉투는 성공인데 data 가 비어 오는 경우 — 그대로 넘기면 화면이 빈 흰 카드로 굳는다
  if (d == null) throw new Error('시험지 내용을 받지 못했습니다.');
  return d;
}

/** 이 문항이 삭제된 문제인가 — 서버가 명시로 true 라 한 것만(계약 §1-3) */
export const isDeletedItem = (item) => item?.is_deleted === true;

/**
 * 이 시험지에 든 삭제 문항 수(계약 §1-3).
 * 최상위 deleted_item_count 를 먼저 믿고, 없으면 items 를 직접 센다 — 배포 이전에 만들어져
 * 그 키가 없는 응답이나, 필드 없이 상세만 들고 있는 화면에서도 같은 답을 내게.
 */
export function deletedItemCount(paper) {
  const n = Number(paper?.deleted_item_count);
  if (Number.isFinite(n)) return n;
  const items = Array.isArray(paper?.items) ? paper.items : [];
  return items.filter(isDeletedItem).length;
}

/**
 * 저장된 시험지 상세 → 공용 PrintPreviewModal 입력.
 *
 * 출력은 일부 문항만 조용히 빼고 성공하면 안 된다. 목록 요약에는 문항 id가 없으므로 상세를
 * 읽은 뒤 여기서 한 번에 검증하고, 상세 `items` 순서를 그대로 `problems_list`에 얼려 둔다.
 * `preset_id`는 의도적으로 만들지 않는다 — 그 키가 있으면 레거시 프리셋 인쇄 API로 갈라진다.
 */
export function paperPrintDataOf(paper, fallbackTitle = '') {
  if (!Array.isArray(paper?.items)) {
    throw new Error('시험지 문항 목록을 읽지 못해 미리보기를 열지 않았습니다.');
  }
  if (paper.items.length === 0) {
    throw new Error('문항이 없는 시험지는 미리보거나 인쇄할 수 없습니다.');
  }

  const deleted = deletedItemCount(paper);
  if (deleted > 0) {
    throw new Error(`삭제된 문항 ${deleted}개가 들어 있어 미리보기를 열지 않았습니다 — 해당 문항을 빼고 저장해 주세요.`);
  }

  const invalid = [];
  const ids = paper.items.map((item, index) => {
    const raw = item?.problem_id;
    const id = typeof raw === 'boolean' ? NaN : Number(raw);
    if (!Number.isSafeInteger(id) || id <= 0) invalid.push(index + 1);
    return id;
  });
  if (invalid.length > 0) {
    throw new Error(`문항 번호를 확인할 수 없는 항목 ${invalid.length}개가 있어 미리보기를 열지 않았습니다.`);
  }

  const seen = new Set();
  const duplicates = new Set();
  ids.forEach((id) => {
    if (seen.has(id)) duplicates.add(id);
    else seen.add(id);
  });
  if (duplicates.size > 0) {
    const shown = Array.from(duplicates).slice(0, 5).map((id) => `#${id}`).join(', ');
    throw new Error(`같은 문항이 중복되어 미리보기를 열지 않았습니다 (${shown}).`);
  }

  const rawTitle = typeof paper.title === 'string' ? paper.title : fallbackTitle;
  return {
    ...(paper.paper_id != null || paper.id != null
      ? { printContext: { kind: 'paper', id: String(paper.paper_id ?? paper.id) } } : {}),
    problems_list: ids.map((problemId) => ({ problem_id: problemId })),
    preset_name: typeof rawTitle === 'string' ? rawTitle.trim() : '',
  };
}

/** 생성 — POST /papers {title, description?, items:[{problem_id, points?}]}. items 순서 = 출제 순서 */
export async function createPaper({ title, description, items }, getAccessToken) {
  const body = { title, items };
  if (description != null && description !== '') body.description = description;
  return requestJsonWithStatus(`${EP}/papers`, { payload: body }, getAccessToken, '시험지를 만들지 못했습니다.');
}

/**
 * 생성 + 즉시 배정 — POST /papers/create-and-assign (authoring §1)
 * → 201 {paper_id, paper_uuid, paper_revision, assigned:[{student_id, assignment_id, assignment_uuid, status}]}
 * 시험지를 먼저 만들고 나중에 여러 학생에게 줄 것이면 createPaper → assignPaper 를 쓴다.
 *
 * 409(계약 §1-3) — assignPaper 와 같은 두 갈래가 온다:
 *   deleted_items{problem_ids} · missing_difficulty{problem_ids}. describeApiError 로 문구를 만든다.
 * 404 — soft 삭제된 학생 id 가 섞였을 때.
 * 400 — 같은 problem_id 두 번 · title 비문자열(null 포함) · problem_id/points 에 boolean.
 */
export async function createAndAssignPaper({ title, description, items, studentIds, examInfo }, getAccessToken) {
  const body = {
    title,
    items: Array.isArray(items) ? items : [],
    student_ids: Array.isArray(studentIds) ? studentIds : [],
    ...examInfoBody(examInfo),
  };
  if (description != null && description !== '') body.description = description;
  return requestJsonWithStatus(`${EP}/papers/create-and-assign`, { payload: body }, getAccessToken, '시험지를 만들어 배정하지 못했습니다.');
}

/**
 * 수정 — PATCH /papers/{id}. 보낸 키만. 변경되면 이력이 쌓이고 revision 이 오른다
 * 400(계약 §1-3): 같은 problem_id 두 번 · title 비문자열 · problem_id/points 에 boolean.
 */
export async function updatePaper(paperId, patch, getAccessToken) {
  return requestJsonWithStatus(`${EP}/papers/${paperId}`, { method: 'PATCH', payload: patch }, getAccessToken, '시험지를 수정하지 못했습니다.');
}

/**
 * 삭제(soft) — DELETE /papers/{id}
 * 409 `{reason:'has_assignments', assignment_count:n}` — **배정(미삭제, 마감분 포함)이 하나라도 있으면 못 지운다**
 * (오너 결정 2026-08-26, 계약 §10). 화면은 배정 수>0 이면 삭제 버튼을 아예 비활성으로 두고 이유를 적어야 한다 —
 * 배정 단건 취소 API 가 없어서, 한 번 배포한 시험지는 되돌릴 길이 없기 때문이다.
 */
export async function deletePaper(paperId, getAccessToken) {
  return requestJsonWithStatus(`${EP}/papers/${paperId}`, { method: 'DELETE' }, getAccessToken, '시험지를 삭제하지 못했습니다.');
}

/** 변경 이력 — GET /papers/{id}/revisions (최신순). created_at 은 §1-1 로 'Z' 가 붙어 온다 */
export async function fetchRevisions(paperId, getAccessToken) {
  const res = await fetchWithTokenRefresh(`${EP}/papers/${paperId}/revisions`, {}, getAccessToken);
  const d = unwrap(res, '변경 이력을 불러오지 못했습니다.') || {};
  return toItems(d.revisions ? { items: d.revisions } : d);
}

/**
 * 되돌리기 — POST /papers/{id}/revert {revision}. 과거 판을 현재로(append-only)
 * 409 `reason:'deleted_items'`(계약 §1-3) — 그 과거 판에 **그 뒤로 삭제된 문항**이 들어 있으면 막힌다.
 * 되돌릴 수 없는 판이라는 뜻이지 이력이 사라진 것은 아니다(화면 문구가 이 둘을 섞으면 안 된다).
 */
export async function revertPaper(paperId, revision, getAccessToken) {
  return requestJsonWithStatus(`${EP}/papers/${paperId}/revert`, { payload: { revision } }, getAccessToken, '되돌리지 못했습니다.');
}

// ── 폴더 (계약 §2 — 계층. 1세대는 BE-20260825-folders.md) ──────────────────
//
// 레거시 시험제작의 '폴더'는 라벨로 묶어 보여 주던 것이었지만, 여기 폴더는 **정식 소유 폴더**다.
// 시험지는 폴더 0~1개에 속하고 무소속으로 존재할 수 있다. 폴더를 지워도 시험지는 살아남는다 —
// 이 차이를 화면 문구가 반드시 말해야 한다(안 그러면 폴더 삭제를 시험지 삭제로 오해한다).
//
// 2026-08-26 부터 **폴더 안에 폴더**가 된다(깊이 최대 3, 최상위=1). 순환 금지.
// **삭제하면 하위 폴더와 시험지가 한 단계 위로 올라간다**(최상위 폴더를 지우면 시험지는 무소속) —
// 시험지와 마찬가지로 아무것도 잃지 않는 원칙이다. 삭제 확인 문구는 "하위 폴더와 시험지는 상위 폴더로 이동합니다".

/** 폴더 깊이 상한(계약 §2). 화면은 이 값으로 "여기에는 더 못 만듭니다"·부모 후보 제외를 판정한다 */
export const MAX_FOLDER_DEPTH = 3;

/** 폴더 id 키 정규화 — 문자열 '12' 와 숫자 12 가 섞여 와도 부모-자식이 서로를 못 찾는 일이 없게 */
const folderKey = (v) => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : String(v);
};

/**
 * 폴더 이름 비교 — **화면의 폴더 순서는 이 규칙 하나다**(오너 결정 2026-09-14: 정렬 버튼 없이 늘 폴더명 오름차순).
 * 가나다순이고 숫자는 크기순이다 — 「2학기」가 「10학기」 앞(강의 개념 목록 창 가나다순 conceptPick 과 같은 비교).
 * 글자 그대로 비교하면 「1월 · 10월 · 11월 · 12월 · 2월」로 선다.
 * 앞뒤 공백은 빼고 본다 — 화면은 이름을 공백 없이 찍으므로(folderLabel · buildFolderPathIndex), 빼지 않으면
 * 보이는 글자와 다른 자리에 선다.
 */
export const compareFolderNames = (a, b) => String(a ?? '').trim().localeCompare(String(b ?? '').trim(), 'ko', { numeric: true });

/**
 * 형제 정렬 — 폴더명 오름차순(compareFolderNames), 이름이 같으면 folder_id(= 서버 seq, 만든 순).
 * 같은 이름 둘이 다시 읽을 때마다 자리를 바꾸지 않게 끝을 id 로 묶는다.
 * 서버 순서(order_no → seq)는 **쓰지 않는다** — 화면이 order_no 를 넣은 적이 없어 사실상 만든 순이었고,
 * 오너가 원한 것은 이름순이다. 폴더 나무(sortFolderTree)와 폴더 고르기(buildFolderTree)가 이 함수 하나를 쓴다.
 */
function compareFolders(a, b) {
  return compareFolderNames(a?.name, b?.name)
    || ((Number(a?.folder_id ?? a?.id) || 0) - (Number(b?.folder_id ?? b?.id) || 0));
}

/**
 * 폴더 나무를 폴더명 오름차순으로 다시 세운 **사본** — 최상위·하위 모두 단마다 compareFolders 로 줄 세운다.
 * 원본은 건드리지 않는다: FolderTree 는 서버 tree 를 state 로 쥐고 그 위에서 이름 바꾸기(patchNode)·깊이 판정을 하므로,
 * 그리는 자리만 이 사본을 쓰면 이름을 바꾼 즉시 새 자리로 간다(다시 읽기를 기다리지 않는다).
 * children 이 없거나 배열이 아닌 노드는 빈 배열로 받친다.
 */
export function sortFolderTree(nodes) {
  return (Array.isArray(nodes) ? nodes : [])
    .map((n) => ({ ...n, children: sortFolderTree(n?.children) }))
    .sort(compareFolders);
}

/** startId 에서 부모를 타고 올라가다 targetId 를 만나는가 — 순환이면 seen 이 끊어 준다 */
function hasAncestor(byId, startId, targetId) {
  const seen = new Set();
  let cur = startId;
  while (cur != null && !seen.has(cur)) {
    if (cur === targetId) return true;
    seen.add(cur);
    cur = folderKey(byId.get(cur)?.parent_id);
  }
  return false;
}

/**
 * 평면 목록 → children[] 중첩(계약 §2).
 * 서버가 `tree` 를 함께 주므로 **평소에는 쓰이지 않는 폴백**이다. 옛 배포·중간 배포에서 tree 가
 * 비어 올 때 트리 화면이 통째로 빈 채로 굳는 것을 막는다.
 *
 * 망가진 데이터에도 그림은 그린다 — 화면은 데이터가 이상하다고 멈추는 자리가 아니다:
 *  - **고아**(parent_id 가 목록에 없음. mine=1 로 부모만 걸러진 경우가 실제로 난다) → 최상위로 올린다.
 *    안 그러면 그 가지가 통째로 사라져 "폴더가 없어졌다"로 읽힌다.
 *  - **순환**(A→B→A. 서버가 막지만 백필·수동 수정으로 생길 수 있다) → 고리에 걸린 쪽을 최상위로.
 *    이 방어가 없으면 재귀가 끝나지 않아 탭이 멈춘다.
 */
export function buildFolderTree(items) {
  const list = Array.isArray(items) ? items : [];
  const byId = new Map();
  list.forEach((f) => {
    const id = folderKey(f?.folder_id ?? f?.id);
    if (id == null || byId.has(id)) return; // id 없는 행·중복 id 는 트리에 넣을 자리가 없다
    byId.set(id, { ...f, children: [] });
  });
  const roots = [];
  byId.forEach((node, id) => {
    const parentId = folderKey(node.parent_id);
    const parent = parentId != null ? byId.get(parentId) : null;
    if (!parent || parentId === id || hasAncestor(byId, parentId, id)) {
      roots.push(node);
      return;
    }
    parent.children.push(node);
  });
  const sortDeep = (nodes) => {
    nodes.sort(compareFolders);
    nodes.forEach((n) => sortDeep(n.children));
  };
  sortDeep(roots);
  return roots;
}

/**
 * 폴더 id → **경로**(루트→잎 이름 배열). 평면 items 를 한 번 훑어 지도로 만든다.
 *
 * 왜 필요한가 — 시험지 요약이 싣고 오는 것은 `folder_name` **하나**뿐이라(계약 §2 말미),
 * 목록에서 「2학기」가 두 줄 서 있으면 어느 부모 밑인지 화면에 남는 단서가 없다.
 * 계층이 생긴 뒤(깊이 최대 3)로는 이름만으로는 소속을 말하지 못한다.
 *
 * 순환·고아를 만나도 멈춘다(buildFolderTree 와 같은 방어) — 서버가 잘못 주더라도 화면은 돌아야 한다.
 * 경로가 비어 나오는 id(목록에 없는 폴더)는 지도에 넣지 않는다 — 부르는 쪽이 folder_name 으로 받친다.
 */
export function buildFolderPathIndex(items) {
  const byId = new Map();
  (Array.isArray(items) ? items : []).forEach((f) => {
    const id = f?.folder_id ?? f?.id;
    if (id != null) byId.set(String(id), f);
  });
  const out = new Map();
  byId.forEach((_, key) => {
    const names = [];
    const seen = new Set();
    let cur = key;
    while (cur != null && !seen.has(cur)) {
      seen.add(cur);
      const node = byId.get(cur);
      if (node == null) break;
      names.unshift(String(node.name ?? '').trim() || '(이름 없음)');
      const parent = node.parent_id;
      cur = parent == null ? null : String(parent);
    }
    if (names.length > 0) out.set(key, names);
  });
  return out;
}

/**
 * 폴더 목록 — GET /folders?mine → {items:[...], tree:[...], unfiled_count}
 * items[] 는 **평면**이고 각 항목에 `parent_id:int|null`, `depth:1~3`, `paper_count`(직접 소속),
 * `total_paper_count`(자손 포함), `child_count` 가 있다(계약 §2).
 * tree 는 **서버가 준 모양 그대로** 돌려준다(없을 때만 buildFolderTree 로 채운다). 순서는 여기서 바꾸지 않는다 —
 * 화면의 폴더 순서는 폴더명 오름차순이고(compareFolders, 오너 결정 2026-09-14) 그리는 쪽이 sortFolderTree 로 세운다.
 * 서버 순서(order_no null 마지막→order_no→seq)는 화면이 order_no 를 넣은 적이 없어 사실상 만든 순이다.
 *
 * 소유 — 항목과 tree 노드 **양쪽**에 `author_uuid` · `author_name` · `is_mine` 이 실린다
 * (BE-20260829-reply-fe-0828.md §3 3-1). 잠금 판정은 `is_mine` 하나로 한다(§1 2-2) —
 * `?mine=1` 을 한 번 더 불러 대조하던 길은 걷었다(FolderTree 의 isMine).
 * `mine` 인자는 「내 폴더만 보기」를 위해 남겨 둔 것이지 잠금 판정용이 아니다.
 */
export async function listFolders({ mine = false } = {}, getAccessToken) {
  const params = new URLSearchParams();
  if (mine) params.append('mine', '1');
  const qs = params.toString();
  const res = await fetchWithTokenRefresh(`${EP}/folders${qs ? `?${qs}` : ''}`, {}, getAccessToken);
  const d = unwrap(res, '폴더를 불러오지 못했습니다.') || {};
  const items = toItems(d);
  const tree = Array.isArray(d.tree) ? d.tree : buildFolderTree(items);
  return { items, tree, unfiledCount: d.unfiled_count ?? 0 };
}

/**
 * 폴더 생성 — POST /folders {name, parent_id?, order_no?} (계약 §2)
 * parentId 를 안 주면(또는 null) **최상위**다. 키 자체를 빼는 이유는 다른 곳과 같다 —
 * "안 정했다"와 "비운다"를 서버가 구분하지 않아도 되게.
 * 404 = 그 parent 가 없음 · 400 = 깊이 3 초과.
 */
export async function createFolder({ name, parentId, orderNo }, getAccessToken) {
  const body = { name };
  if (parentId != null) body.parent_id = parentId;
  if (orderNo != null) body.order_no = orderNo;
  return requestJsonWithStatus(`${EP}/folders`, { payload: body }, getAccessToken, '폴더를 만들지 못했습니다.');
}

/**
 * 폴더 수정 — PATCH /folders/{id} {name?, is_open?, order_no?, parent_id?}. 보낸 키만.
 * parent_id 는 **이동**이고 null 이면 최상위로 올린다(계약 §2). 이동만 할 것이면 moveFolder 가 읽기 쉽다.
 * 400 = 자기 자신·자기 자손으로 옮김 · 이동 뒤 서브트리 깊이가 3 을 넘음.
 */
export async function updateFolder(folderId, patch, getAccessToken) {
  return requestJsonWithStatus(`${EP}/folders/${folderId}`, { method: 'PATCH', payload: patch }, getAccessToken, '폴더를 수정하지 못했습니다.');
}

/**
 * 폴더 이동 — PATCH /folders/{id} {parent_id} (계약 §2)
 * parentId 를 비우면 **최상위로**. 여기서는 키를 빼지 않고 null 을 명시로 보낸다 —
 * 이동은 "정하지 않음"이 없는 동작이라, 키가 빠지면 아무 일도 일어나지 않아 조용히 실패한다.
 * 400 = 자기 자신·자기 자손 · 이동 뒤 깊이>3(화면은 MAX_FOLDER_DEPTH 로 미리 거른다).
 */
export async function moveFolder(folderId, parentId, getAccessToken) {
  return requestJsonWithStatus(
    `${EP}/folders/${folderId}`,
    { method: 'PATCH', payload: { parent_id: parentId ?? null } },
    getAccessToken,
    '폴더를 옮기지 못했습니다.',
  );
}

/**
 * 폴더 삭제 — DELETE /folders/{id} → {folder_id, detached_papers, lifted_folders, parent_id}
 * ⚠ **아무것도 지워지지 않는다.** 시험지(detached_papers 개)와 하위 폴더(lifted_folders 개)가
 * 한 단계 위(parent_id, null 이면 최상위·무소속)로 **올라갈 뿐**이다(계약 §2).
 * 화면은 두 수를 그대로 말해야 한다 — "N개가 상위 폴더로 이동했습니다".
 */
export async function deleteFolder(folderId, getAccessToken) {
  return requestJsonWithStatus(`${EP}/folders/${folderId}`, { method: 'DELETE' }, getAccessToken, '폴더를 삭제하지 못했습니다.');
}

/**
 * 폴더 공유 설정 — PUT /folders/{id}/shares
 * (BE-20260910-reply-share-all-teachers.md §2-3 · BE-20260910-reply-teacher-access-folder-sharing.md §3)
 *
 * body 는 두 키고 **하나 이상**이어야 한다(둘 다 없으면 400):
 *   all_teachers `'view'|'edit'|null` — 「교사 전원」 공유 상태. **null 이 해제**다.
 *   items `[{user_uuid, permission}]` — 개별 공유 **목록 전체 대체**(부분 추가가 아니다).
 *
 * **보낸 키만 바뀐다.** 그래서 판정이 `!= null` 이 아니라 `!== undefined` 다 — null 은 「해제」라는
 * **값**이고, 키 없음이 「그대로」다. 이 파일에는 두 관례가 이미 있다: createFolder 는 값 없는 키를
 * 통째로 빼고(「안 정했다」와 「비운다」를 서버가 구분하지 않아도 되게), moveFolder 는 null 을 명시로
 * 싣는다(이동에는 「정하지 않음」이 없어, 키가 빠지면 조용히 아무 일도 안 일어난다).
 * 이 함수는 **둘을 동시에** 지켜야 한다 — 안 준 키는 빼고, 준 null 은 싣는다.
 * `!= null` 로 쓰면 [공유 취소]가 아무 키도 안 실어 400 이 되고, 거꾸로 두 키를 늘 실으면
 * all_teachers 만 바꾸려는 호출이 items 를 빈 배열로 덮어 **개별 공유를 통째로 지운다.**
 *
 * 오류 갈래(문구는 describeApiError 가 만든다):
 *   400 `not_root`(+`root_folder_id`) 최상위 폴더가 아님 — 그 폴더로 다시 부르면 된다
 *   400 `invalid_grantee`(+`user_uuids`) items 대상이 스태프 계정이 아님·없음·삭제됨
 *   403 소유자·매니저가 아님(교사는 남이 만든 폴더를 공유할 수 없다)
 *
 * 화면이 쓰는 호출은 **둘뿐이다** — 공유 `{allTeachers:'view'}` · 취소 `{allTeachers:null}`
 * (오너 규칙: 대상은 교사 전원, 등급은 view 고정, 대상 고르기·등급 선택을 두지 않는다).
 * `items` 를 보내지 않으므로 **개별 공유는 그대로 남는다**(계약 §3 Q4) — [공유 취소]는 「전원 공유」만
 * 끄고, 손으로 걸어 둔 개별 공유까지 없애지 않는다. 이 두 호출에는 items 가 영영 필요 없지만
 * 인자를 남겨 둔 것은, 개별 공유 화면이 생기면 같은 엔드포인트를 이 함수로 부르기 때문이다.
 *
 * 읽는 GET(`/shares`)은 **만들지 않았다** — 폴더 목록 items[]·tree 노드·생성·수정 응답에 `all_teachers`
 * 가 실려 와 화면이 그 값으로 상태를 그린다(§2-3). 쓰지 않을 함수를 두면 다음 사람이 어느 쪽이
 * 정본인지 다시 물어야 한다.
 */
export async function putFolderShares(folderId, { allTeachers, items } = {}, getAccessToken) {
  const body = {};
  if (allTeachers !== undefined) body.all_teachers = allTeachers;
  if (items !== undefined) body.items = items;
  return requestJsonWithStatus(
    `${EP}/folders/${folderId}/shares`,
    { method: 'PUT', payload: body },
    getAccessToken,
    '공유 설정을 저장하지 못했습니다.',
  );
}

/**
 * 시험지 폴더 이동 — POST /papers/move {paper_ids, folder_id}
 * folder_id 는 **명시 필수**다. null 이 "폴더에서 빼기"이므로 키를 빠뜨리면 400 이다.
 */
export async function movePapers(paperIds, folderId, getAccessToken) {
  return requestJsonWithStatus(
    `${EP}/papers/move`,
    { payload: { paper_ids: paperIds, folder_id: folderId ?? null } },
    getAccessToken,
    '시험지를 옮기지 못했습니다.',
  );
}

/**
 * 시험지 복사 — POST /papers/{id}/copy {folder_id?, title?}
 * 사본은 원본과 링크가 없는 **독립 시험지**다(derived_from=null). 한쪽을 고쳐도 다른 쪽은 그대로.
 * 409 `reason:'deleted_items'`(계약 §1-3) — 원본에 삭제된 문항이 있으면 복사도 막힌다.
 */
export async function copyPaper(paperId, { folderId, title } = {}, getAccessToken) {
  const body = {};
  if (folderId != null) body.folder_id = folderId;
  if (title != null && title !== '') body.title = title;
  return requestJsonWithStatus(`${EP}/papers/${paperId}/copy`, { payload: body }, getAccessToken, '복사하지 못했습니다.');
}

/** 무소속 가상 폴더의 folder_id 값 — 계약이 문자열 'none' 을 쓴다 */
export const UNFILED = 'none';

// ── 배포·채점 ─────────────────────────────────────────────────────────────

/**
 * 배포 — POST /papers/{id}/assign {student_ids, 시험정보?} (authoring §4)
 * 이 시점 문항·본문이 스냅샷으로 고정된다.
 * → 201 {paper_id, paper_revision, assigned:[{student_id, assignment_id, assignment_uuid, status}]}
 * assignment_id 가 항상 오므로(§2-9) 방금 만든 배포본을 바로 열 수 있다.
 *
 * 409(계약 §1-3) — 두 갈래가 err.data.reason 으로 온다:
 *   `deleted_items{problem_ids}` 삭제된 문항이 들어 있음 ·
 *   `missing_difficulty{problem_ids}` 난이도(분석)가 없는 문항이 있음 → 배포 전 분석 필요.
 *   난이도 쪽은 문제 검색 응답의 `need_difficulty` 배지로 **고르는 단계에서** 미리 안내된다.
 * 404 — soft 삭제된 학생 id.
 */
export async function assignPaper(paperId, { studentIds, examInfo } = {}, getAccessToken) {
  const payload = { student_ids: Array.isArray(studentIds) ? studentIds : [], ...examInfoBody(examInfo) };
  return requestJsonWithStatus(`${EP}/papers/${paperId}/assign`, { payload }, getAccessToken, '배포하지 못했습니다.');
}

/**
 * 이 시험지의 배포 목록 — GET /papers/{id}/assignments
 * (contract-answers §1-1 · 2026-08-29 회신 §1-1 로 `paper_revision` 추가
 *  · 2026-09-06 회신 §1 로 채점 집계 3개와 학생 신상 6개 추가)
 * items[] = {assignment_id, assignment_uuid, paper_revision, student_uuid, student_name,
 *            exam_type, exam_round, exam_date, open_at, close_at, status, is_closed,
 *            difficulty, total_items, graded, assigned_at, closed_at,
 *            correct_count, wrong_count, unanswered_count,
 *            student_id, school_name, grade, in_charge_teacher, in_charge_teacher_uuid, lecture}
 * `correct_count`+`wrong_count`+`unanswered_count` = `graded` 이고, **마감 여부와 무관하게**
 * 배정마다 센다(2026-09-06 회신 §1). 요약 띠의 모집단은 화면이 `is_closed` 로 고른다.
 * 학생 신상 6개는 학생이 삭제됐으면 각각 null 로 온다 — 화면이 '-' 로 그린다.
 * 페이지네이션은 **없다**(회신 §0 D — 전량, seq 내림차순). 아래 page/totalPages 는 방어값이다.
 * `paper_revision` = **배정 시점에 굳은 시험지 판**이다. 시험지를 고쳐 revision 이 올라가도
 * 이미 나간 배정의 값은 그대로다(회신 §1-1 확인) — 화면이 「현재 판」과 견줘 말하는 근거가 이것이다.
 * 시각은 전부 'Z' 가 붙은 UTC 다(§1-1) — fmtServerDateTime 으로 찍는다.
 */
export async function fetchPaperAssignments(paperId, getAccessToken) {
  const res = await fetchWithTokenRefresh(`${EP}/papers/${paperId}/assignments`, {}, getAccessToken);
  const d = unwrap(res, '배포 목록을 불러오지 못했습니다.') || {};
  const items = toItems(d);
  // 계약에 페이지네이션이 없어 전량 전제다(화면도 전부 그린다). 서버가 나중에 페이지 값을
  // 실어 오면 그때 페이저를 붙일 수 있도록 값만 받아 둔다.
  return { items, page: d.page ?? 1, totalPages: d.total_pages ?? 1 };
}

/**
 * 배포 상세 — GET /assignments/{aid} → 스냅샷 문항 + 채점 현황. 마감 판정은 is_closed 또는 status===200
 * 최상위에 `paper_revision` 이 온다 = 배정 시점에 굳은 시험지 판(회신 §1-1 — 「원래 있었음」).
 *
 * 2026-09-06 회신 §2 로 문항마다 `answer`·`answer_source` 가 실린다.
 *   answer        은행 저장값 그대로 — 객관식 원문자 ①~⑤ · 주관식 값 문자열(bare LaTeX 가능).
 *                 정답이 없거나 더미('.')면 **null** → 화면은 「정답 정보 없음」으로 적는다.
 *   answer_source 'snapshot' = 배포 시점 정답(2026-09-06 이후 배정) ·
 *                 'current'  = 그 전 배정이라 스냅샷이 없어 **현재 은행 정답**으로 채운 값.
 *                 소급이 불가해 그렇게 정했다(회신 §4-1). 문제를 나중에 고쳤으면 'current' 인 옛
 *                 배정만 채점 기준이 흔들릴 수 있으므로 화면이 그 사실을 꼬리표로 말한다.
 * 최상위에도 학생 신상 6필드와 채점 집계 3개가 목록과 같은 이름으로 동봉된다.
 */
export async function fetchAssignment(assignmentId, getAccessToken) {
  const res = await fetchWithTokenRefresh(`${EP}/assignments/${assignmentId}`, {}, getAccessToken);
  const d = unwrap(res, '배포본을 불러오지 못했습니다.');
  if (d == null) throw new Error('배포본 내용을 받지 못했습니다.');
  return d;
}

/**
 * 채점 — PATCH /assignments/{aid}/results {results:[{problem_id, result}]}
 * result 는 JSON 3-state 고정: true(O) / false(X) / null(미채점). 부분 채점 허용.
 * **1·0 을 보내면 400 이다**(계약 §1-2) — 숫자로 직렬화하지 마라.
 * 응답 = 갱신된 배정 상세 전체(§2-9) — 저장 뒤 다시 GET 할 필요가 없다.
 *
 * ⚠ **채점해도 status 가 200(마감)으로 바뀌지 않는다**(계약 §1-2 A2). 50(예약)·100(진행)이 그대로 유지된다.
 * 예전에는 다 채점하면 status 가 200 이 돼 화면이 "마감"으로 읽었다 — **마감 판정은 `is_closed` 또는
 * `status===200` 뿐**이고, 채점 진행률은 `graded`/`total_items` 로 따로 말해야 한다.
 * 409 `reason:'scheduled'` — open_at 이 아직 오지 않은 예약 배정은 채점할 수 없다.
 */
export async function patchResults(assignmentId, results, getAccessToken) {
  return requestJsonWithStatus(`${EP}/assignments/${assignmentId}/results`, { method: 'PATCH', payload: { results } }, getAccessToken, '채점을 저장하지 못했습니다.');
}

/**
 * 마감 — POST /assignments/{aid}/close. 전 문항 채점을 강제하지 않는다(채점분만 리포트 반영).
 * 409 `reason:'scheduled'`(계약 §1-2) — 아직 오픈 전(예약)인 배정은 마감할 수 없다.
 */
export async function closeAssignment(assignmentId, getAccessToken) {
  return requestJsonWithStatus(`${EP}/assignments/${assignmentId}/close`, {}, getAccessToken, '채점을 완료하지 못했습니다.');
}

/**
 * 재오픈 — POST /assignments/{aid}/reopen (계약 §1-2 A1)
 * `close_at`(자동마감 예약)까지 **해제**하고 status 를 되돌린다(open_at 미도래면 50 예약, 아니면 100 진행).
 * 이전에는 예약이 남아 60초 안에 다시 마감되던 결함이 있었다 — 그래서 화면의 "다시 열렸습니다"가 거짓말이었다.
 * **423 = 마감되지 않은 배정에 불렀을 때**(reason 없음. describeApiError 가 httpStatus 로 문구를 만든다).
 * 재오픈하면 그 배정은 **즉시 라이브 리포트에서 빠진다**(마감분만 집계). 저장된 KY Report 스냅샷은 불변이고,
 * 다시 [마감]하면 재편입된다(contract-answers §3-3) — 화면 문구가 이 셋을 말해야 한다.
 */
export async function reopenAssignment(assignmentId, getAccessToken) {
  return requestJsonWithStatus(`${EP}/assignments/${assignmentId}/reopen`, {}, getAccessToken, '채점을 다시 수정할 수 있도록 열지 못했습니다.');
}

/**
 * **전체 배정 목록** — `GET /api/v2/exam-papers/assignments` (2026-09-06 신설 · dev `537792e`,
 * 회신 `docs/exam-builder-v2/BE-20260906-reply-assignment-global-list.md`).
 *
 * 종전에는 이 목록이 없어 화면이 시험지를 돌며 조립했다(1+N). 이제 한 번에 온다 —
 * 행은 시험지별 목록과 **같은 함수**가 만들고(`_assignment_rows`), 거기에 시험지 쪽 값 넷
 * (`paper_id`·`paper_title`·`paper_current_revision`·`paper_uuid`)이 얹혀 온다.
 *
 * **범위는 서버가 좁히지 않는다**(회신 §2 4-2) — 어느 토큰이든 학원 전체가 오고 응답 `scope` 는 늘 `'all'`.
 * 교사 화면에서 좁혀야 하면 `inChargeTeacher`(이름) 또는 `studentId` 로 화면이 건다.
 *
 * 허용값 밖은 **400**이다(정렬 키·차례·참거짓·정수 id·날짜·100자 초과) — 시험지 목록의 `sort` 와 같은 규약이라
 * 여기서도 **아는 값만 싣는다**. 빈 칸은 아예 보내지 않는다(빈 문자열을 보내면 그 조건으로 걸러진다).
 * 삭제된 배정은 서버가 제외한다. 담당 교사 이름이 없으면 빈 목록을 200 으로 준다.
 *
 * `per_page` 는 **5~100** 이다(회신 §2 4-5) — 학생 검색의 200 과 다르니 그 자를 가져오지 마라.
 * 범위 밖으로 부르면 400 이고 목록이 통째로 실패한다. 값을 여기서 조이지 않는 이유는 형제 함수와
 * 같다(`searchStudents`) — 부른 쪽이 청한 수와 다른 수로 조용히 바꾸면, 잘못 부른 것이 드러나지 않는다.
 */
export const ASSIGNMENT_ORDER_BYS = ['assigned_at', 'closed_at', 'seq'];
export const ASSIGNMENT_ORDERS = ['desc', 'asc'];
export const ASSIGNMENT_STATUSES = [50, 100, 200];

/**
 * 글자 조건의 길이 상한(회신 §1 — 넘으면 400). 값을 여기서 잘라 내지 **않는다** —
 * 조용히 자르면 사용자가 친 것과 다른 조건으로 찾아 놓고 결과를 「맞다」고 내밀게 된다.
 * 대신 화면이 입력칸의 `maxLength` 로 애초에 못 넘게 막는다(`AssignmentFeedPanel`).
 */
export const ASSIGNMENT_FILTER_MAXLEN = 100;

export async function listAssignments(
  {
    page = 1, perPage = 20, orderBy = '', order = '', isClosed = null, status = null,
    assignmentId = null, paperId = null, studentId = null,
    q = '', schoolName = '', lecture = '', inChargeTeacher = '', dateFrom = '', dateTo = '',
  } = {},
  getAccessToken,
) {
  const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
  if (ASSIGNMENT_ORDER_BYS.includes(orderBy)) params.append('order_by', orderBy);
  if (ASSIGNMENT_ORDERS.includes(order)) params.append('order', order);
  if (isClosed != null) params.append('is_closed', isClosed ? 'true' : 'false');
  if (ASSIGNMENT_STATUSES.includes(Number(status))) params.append('status', String(Number(status)));
  // 번호 셋은 **손보지 않고 그대로** 보낸다 — 정수가 아니면 서버가 400 으로 말한다(listPapers 의 problem_id 와 같은 근거).
  // 여기서 조용히 빼면 조건 없는 전체 목록이 돌아와 「그 번호의 배정이 이렇게 많다」가 된다.
  [['assignment_id', assignmentId], ['paper_id', paperId], ['student_id', studentId]].forEach(([k, v]) => {
    if (v != null && String(v).trim() !== '') params.append(k, String(v).trim());
  });
  [['q', q], ['school_name', schoolName], ['lecture', lecture], ['in_charge_teacher', inChargeTeacher],
    ['date_from', dateFrom], ['date_to', dateTo]].forEach(([k, v]) => {
    const t = typeof v === 'string' ? v.trim() : (v == null ? '' : String(v).trim());
    if (t !== '') params.append(k, t);
  });
  const res = await fetchWithTokenRefresh(`${EP}/assignments?${params}`, {}, getAccessToken);
  const d = unwrap(res, '배정 목록을 불러오지 못했습니다.') || {};
  const items = toItems(d);
  return { items, ...pageMeta(d, page, perPage, items), scope: d.scope || null };
}

/**
 * **배정 삭제** — `DELETE /api/v2/exam-papers/assignments/{aid}` (2026-09-06 신설 · dev `537792e`,
 * 회신 `docs/exam-builder-v2/BE-20260906-reply-assignment-delete.md`).
 *
 * 배정 행은 soft delete 이고 그 배정의 **채점 기록(ProblemRecords)은 함께 지워진다**(오너 결정) —
 * 그래야 학생의 개념별 정오답에서도 빠진다. 응답 `deleted_records` 가 그 수다.
 * 되살릴 수 없고, 이미 지운 배정은 404 다(멱등 아님).
 *
 * **마감된 배정은 409 `reason:'closed'`** — 재오픈([채점 수정])을 먼저 해야 한다. 마감분은 리포트에
 * 편입된 상태라 두 걸음이 안전장치다(회신 §4-6). 화면도 서버를 기다리지 않고 먼저 막는다.
 * 지우면 그 시험지의 `assign_count` 가 줄고, 0 이 되면 시험지 삭제 잠금이 풀린다(의도된 연쇄).
 */
export async function deleteAssignment(assignmentId, getAccessToken) {
  return requestJsonWithStatus(`${EP}/assignments/${assignmentId}`, { method: 'DELETE' }, getAccessToken, '배정을 삭제하지 못했습니다.');
}

/**
 * 학생별 배포 목록 — GET /students/{sid}/assignments?is_closed=
 * 행은 배정 상세와 동형이고 paper_id 가 실린다(§2-9) — 학생 화면(테스트 목록)용.
 */
export async function fetchStudentAssignments(studentId, { isClosed = null } = {}, getAccessToken) {
  const params = new URLSearchParams();
  if (isClosed != null) params.append('is_closed', isClosed ? 'true' : 'false');
  const qs = params.toString();
  const res = await fetchWithTokenRefresh(
    `${EP}/students/${studentId}/assignments${qs ? `?${qs}` : ''}`,
    {},
    getAccessToken,
  );
  const d = unwrap(res, '배포 목록을 불러오지 못했습니다.') || {};
  return toItems(d);
}

/**
 * 학생별 배포 목록 **전체** — 학생 엔드포인트는 기본 20건이라, 마감 이력을 계산하는 화면은
 * page/per_page 를 명시하고 끝까지 넘겨야 한다. 한 쪽 크기 100은 서버 상한이고 50쪽(5,000건)에서
 * 방어적으로 멈춘다. total_pages 가 없는 구형 응답은 마지막 짧은 쪽으로 끝을 판별한다.
 *
 * 최신 몇 건만 필요한 피드의 fetchStudentAssignments 와 의미가 달라 별도 함수로 둔다.
 */
const STUDENT_ASSIGNMENTS_PER_PAGE = 100;
const STUDENT_ASSIGNMENTS_MAX_PAGES = 50;

export async function fetchAllStudentAssignments(studentId, { isClosed = null } = {}, getAccessToken) {
  const all = [];
  for (let page = 1; page <= STUDENT_ASSIGNMENTS_MAX_PAGES; page += 1) {
    const params = new URLSearchParams({
      page: String(page),
      per_page: String(STUDENT_ASSIGNMENTS_PER_PAGE),
    });
    if (isClosed != null) params.append('is_closed', isClosed ? 'true' : 'false');

    // 다음 쪽 유무는 직전 응답의 total_pages(없으면 받은 건수)로 판별하므로 순차 호출한다.
    // eslint-disable-next-line no-await-in-loop
    const res = await fetchWithTokenRefresh(
      `${EP}/students/${encodeURIComponent(studentId)}/assignments?${params}`,
      {},
      getAccessToken,
    );
    const d = unwrap(res, '배포 목록을 불러오지 못했습니다.') || {};
    const items = toItems(d);
    all.push(...items);

    const totalPages = d?.total_pages == null ? null : Number(d.total_pages);
    const isLastPage = Number.isFinite(totalPages)
      ? page >= totalPages
      : items.length < STUDENT_ASSIGNMENTS_PER_PAGE;
    if (isLastPage) break;
    // 일부만 돌려주면 오래된 시험이 조용히 사라진다. 방어 상한에 닿으면 부분 성공 대신 명시적으로 실패한다.
    if (page === STUDENT_ASSIGNMENTS_MAX_PAGES) {
      throw new Error('배포 이력이 5,000건을 넘어 전체 목록을 불러오지 못했습니다.');
    }
  }
  return all;
}

// ── AI 복사 배치 (변형 데몬 소유) ──────────────────────────────────────────

/**
 * AI 복사 시작 — 전체 경로는 **POST /api/v2/problem-variation/papers/{paper_id}/ai-copy** 다
 * (BE-20260829-reply-fe-0828.md §3 3-1 이 경로를 본문에 못박았다. 그 전에는 계약이 이름만 적어
 *  경로가 문서에 없었고, 코드는 처음부터 이 경로를 부르고 있었다).
 * body {level, options?, title?, user_prompt?} → {batch_id, item_count}
 */
export async function startAiCopy(paperId, { level, options, title, userPrompt }, getAccessToken) {
  const body = { level };
  if (options && Object.keys(options).length > 0) body.options = options;
  if (title != null && title !== '') body.title = title;
  // 사용자 지시(선택) — 비면 키를 싣지 않는다.
  // 서버 수용 확인됨(회신 §4-1 표 · §6-1 「안 받고 있었음 → 이제 받음」). strip 후 500자 상한이고
  // 초과는 400, 빈 문자열·공백만은 키를 안 보낸 것과 같다(§4-2 5-2).
  // 저장처는 배치의 user_prompt + 그 배치가 만든 각 변형 행의 user_prompt(§5-3 표).
  if (userPrompt && userPrompt.trim()) body.user_prompt = userPrompt.trim();
  return requestJsonWithStatus(`${VAR}/papers/${paperId}/ai-copy`, { payload: body }, getAccessToken, 'AI 복사를 시작하지 못했습니다.');
}

/**
 * 배치 목록 — GET /batches?source_paper_id=&status=&page=&per_page= (contract-answers §1-2)
 * items[] = {batch_id, batch_uuid, title, level, status, source_paper_id, result_paper_id,
 *            counts:{total,done,approved}, created_at}
 * batch_id 를 잃어도 여기서 되찾는다 — 진행 중인 배치로 돌아가는 정식 길.
 */
export async function listBatches({ sourcePaperId = null, status = null, page = 1, perPage = 20 } = {}, getAccessToken) {
  const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
  if (sourcePaperId != null && sourcePaperId !== '') params.append('source_paper_id', String(sourcePaperId));
  if (status != null && status !== '') params.append('status', String(status));
  const res = await fetchWithTokenRefresh(`${VAR}/batches?${params}`, {}, getAccessToken);
  const d = unwrap(res, '배치 목록을 불러오지 못했습니다.') || {};
  const items = toItems(d);
  return { items, ...pageMeta(d, page, perPage, items) };
}

/**
 * 배치 현황 — GET /batches/{bid} → counts + 멤버(items, order_no순·항상 전량)
 * 최상위에 source_paper_id/source_paper_title/result_paper_id/result_paper_title 도 실린다(계약 §4 D8) —
 * 원본·산출 시험지 이름을 화면이 따로 조회하지 않아도 된다.
 */
export async function fetchBatch(batchId, getAccessToken) {
  const res = await fetchWithTokenRefresh(`${VAR}/batches/${batchId}`, {}, getAccessToken);
  const d = unwrap(res, '배치를 불러오지 못했습니다.');
  if (d == null) throw new Error('배치 내용을 받지 못했습니다.');
  return d;
}

/**
 * 조립 — POST /batches/{bid}/assemble → 새 시험지 {paper_id}.
 * 409 는 err.data.reason 으로 갈래가 온다(§2-10): unsettled_members(+variation_ids)·all_rejected·empty_batch·
 * batch_discarded(BE-20261102 회신 §1).
 */
export async function assembleBatch(batchId, getAccessToken) {
  return requestJsonWithStatus(`${VAR}/batches/${batchId}/assemble`, {}, getAccessToken, '조립하지 못했습니다.');
}

/** 배치 status — 100 진행중 / 200 시험지 생성됨 / 400 폐기(BE-20261102 회신 §1) */
export const BATCH_STATUS_ONGOING = 100;
export const BATCH_STATUS_DISCARDED = 400;

/**
 * 배치 폐기 — POST /batches/{bid}/discard (매니저 전용, BE-20261102 회신 §1).
 * 조립 전 배치를 끝낸다: 미승인 문항은 폐기되고 대기 문항은 생성이 취소된다. 승인된 문항·은행 문제는 그대로다.
 * 이미 폐기된 배치도 200 이라 두 번 눌러도 안전하다. 409 는 data.reason 으로 갈린다: already_assembled·busy.
 *
 * 경로 자체가 없는 서버(운영 배포 전)는 영어 404 를 준다 — 그대로 띄우면 뜻을 알 수 없어 여기서 우리 말로 바꾼다.
 */
export async function discardBatch(batchId, getAccessToken) {
  try {
    return await requestJsonWithStatus(`${VAR}/batches/${batchId}/discard`, {}, getAccessToken, '시험지를 폐기하지 못했습니다.');
  } catch (e) {
    if ((e?.httpStatus === 404 || e?.httpStatus === 405) && /requested URL was not found|method is not allowed/i.test(e.message || '')) {
      e.message = '이 서버에는 아직 시험지 폐기 기능이 없습니다 — 서버 배포 뒤 다시 시도해 주세요.';
    }
    throw e;
  }
}

// ── 학생 목록 (v2 — 계약 §7) ──────────────────────────────────────────────

/** in_charge_teacher 에 넣으면 "담당 없음" — 계약이 문자열 'none' 을 쓴다(§7) */
export const STUDENT_NO_TEACHER = 'none';

/**
 * 학생 검색 — GET /api/v2/students (계약 §7)
 * **경로가 하나다.** 범위는 토큰이 정한다 — 매니저·관리자는 전체, 교사는 자기 담당(in_charge_teacher),
 * 그 외 403. 응답 `scope` 가 'all'|'mine' 으로 어느 쪽이 적용됐는지 말해 주므로 화면이 그대로 띄우면 된다.
 * 이전에는 계정 종류로 v1 경로가 갈렸고(`/api/users/student` 대 `/student/allocate` + teacher_id),
 * 그것을 틀리면 **교사 계정에서 담당 학생이 하나도 안 나왔다** — 그 분기가 사라졌으므로
 * **userType·userId 인자는 더 이상 받지 않는다.**
 *
 * 필터 4개 전부 선택·부분검색(ILIKE)이고, `%`·`_` 는 서버가 리터럴로 다룬다:
 *   q → student_name · school → school_name · lecture → lecture · teacher → in_charge_teacher(선생님 **이름**).
 * 빈 값은 키 자체를 안 보낸다(다 비우면 전체 목록). teacher 에 STUDENT_NO_TEACHER 를 넣으면 담당 없는 학생.
 * per_page 는 서버 상한이 200 이다 — 그보다 크게 부르지 마라.
 *
 * 응답 items[] = {student_id:int, student_uuid, student_name, school_name, grade,
 *                 in_charge_teacher(이름|null), in_charge_teacher_uuid, lecture, student_status}. 정렬 이름→id.
 * `student_status` 는 **재원/퇴원이 아니다** — 정수 두 값 `100 일반` / `200 예약 중`
 * (BE-20260829-reply-fe-0828.md §1 2-1). 퇴원은 `is_deleted` 이고 이 목록이 삭제 학생을 애초에
 * 빼고 주므로 **여기 오는 학생은 전부 재원**이다. 휴원 개념은 없다. 표기는 STUDENT_STATUS 참조.
 * ⚠ dev 는 prod 사본이라 실학생 PII 가 보인다.
 */
export async function searchStudents({ q = '', school = '', lecture = '', teacher = '', page = 1, perPage = 20 } = {}, getAccessToken) {
  const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
  const text = (v) => (typeof v === 'string' ? v.trim() : (v == null ? '' : String(v).trim()));
  const name = text(q);
  if (name !== '') params.append('student_name', name);
  const schoolName = text(school);
  if (schoolName !== '') params.append('school_name', schoolName);
  const lectureName = text(lecture);
  if (lectureName !== '') params.append('lecture', lectureName);
  const teacherName = text(teacher);
  if (teacherName !== '') params.append('in_charge_teacher', teacherName);
  const res = await fetchWithTokenRefresh(`${STU}?${params}`, {}, getAccessToken);
  const d = unwrap(res, '학생을 검색하지 못했습니다.') || {};
  const items = toItems(d);
  return { items, ...pageMeta(d, page, perPage, items), scope: d.scope ?? null };
}

export const studentId = (s) => s?.student_id ?? s?.id;
export const studentName = (s) => s?.student_name ?? s?.name ?? '(이름 없음)';

// ── 표시 상수 ─────────────────────────────────────────────────────────────

/** 배치 멤버 정리 여부 — 승인(200)/폐기(400)만 조립 게이트를 통과한다(계약 §4) */
export const isSettled = (m) => m?.review_status === 200 || m?.review_status === 400;

/**
 * 배정 상태 — 50 예약 / 100 진행 / 200 마감(contract-answers §1-1).
 * 예약·마감은 데몬이 open_at·close_at 을 보고 자동으로 넘긴다(authoring §4-1).
 * ⚠ 채점만으로는 200 이 되지 않는다(계약 §1-2) — 마감은 [마감] 또는 close_at 도래로만 온다.
 */
export const ASSIGN_STATUS = {
  50: { text: '예약', cls: 'bg-violet-50 text-violet-700' },
  100: { text: '진행 중', cls: 'bg-green-50 text-green-700' },
  200: { text: '채점 완료', cls: 'bg-primary/10 text-primary' },
};
export function assignStatusDisplay(row) {
  const m = ASSIGN_STATUS[row?.status];
  if (m) return m;
  // status 가 비어 와도 마감 여부는 따로 온다 — 두 갈래만이라도 정확히 말한다
  if (row?.is_closed) return ASSIGN_STATUS[200];
  return { text: '상태 미상', cls: 'bg-gray-50 text-gray-400' };
}

/**
 * 학생 신분 — `student_status` 100 일반 / 200 예약 중(BE-20260829-reply-fe-0828.md §1 2-1).
 *
 * **100 에는 배지를 두지 않는다.** 목록에 오는 학생은 전부 재원이라 100 이 곧 기본값이고,
 * 모두에게 붙는 배지는 아무것도 가르지 않으면서 줄만 시끄럽게 한다. 200 만 말한다.
 *
 * 글자가 「예약」이 아니라 **「예약생」**인 이유: 같은 패널(AssignPanel)이 배정 상태 50 에 이미
 * 「예약」배지를 쓴다(위 ASSIGN_STATUS). 같은 화면에서 한 글자가 위에서는 **학생 신분**, 아래에서는
 * **배포 상태**를 뜻하면 「예약 3명에게 예약 배포」 같은 줄이 생겨 둘 다 못 읽는다.
 * 색도 갈라 둔다 — 배정 배지는 보라(violet), 학생 신분은 호박(amber. 같은 화면의 폴더 알약이 쓰는 값).
 *
 * 예약 중 학생에게 **배포해도 되는지는 회신에 답이 없다** — 그래서 막지도 않고 「됩니다」라고도
 * 말하지 않는다. 화면은 신분만 표시하고 판단은 사람이 한다(다음 왕복에서 물을 것).
 *
 * ⛔ 2026-09-05 현재 **이 배지를 그리는 화면이 없다.** AssignPanel 의 호출부 두 곳을 오너 지시로
 * 잠갔다 — 「모르니까 일단 보여준다」가 되어 보는 사람도 무엇을 판단할지 몰랐다. 사유와 되살리는
 * 조건(FE-20260829-reply-followup Q19 회신)은 AssignPanel 머리의 「⛔ 기능 보류」에 적어 두었다.
 * 정의를 여기 남겨 두는 것은 회신이 오면 그 답에 맞는 모양으로 다시 세우기 위해서다.
 */
export const STUDENT_STATUS = {
  100: { text: null, cls: '' },
  200: { text: '예약생', cls: 'bg-amber-50 text-amber-700' },
};
/** 배지를 그릴 것이 없으면 null — 모르는 값(옛 응답·미상)도 지어내지 않고 아무것도 안 그린다 */
export function studentStatusDisplay(s) {
  const m = STUDENT_STATUS[s?.student_status];
  return m && m.text ? m : null;
}

/** 채점 3-state 표기 */
export const RESULT_LABEL = { true: 'O', false: 'X', null: '—' };
export function resultDisplay(v) {
  if (v === true) return { text: 'O', cls: 'bg-green-100 text-green-800 border-green-200' };
  if (v === false) return { text: 'X', cls: 'bg-red-100 text-red-700 border-red-200' };
  return { text: '미채점', cls: 'bg-gray-50 text-gray-400 border-gray-200' };
}

/**
 * 표 한 칸에 들어가는 짧은 날짜 — `MM-DD`. 레거시 결과표(ExamsByPresetModal:30)와 같은 얼굴이다.
 *
 * `fmtDate` 처럼 앞 10자를 자르지 않는다. 마감 시각(`closed_at`)은 **시각이 붙은 UTC** 라
 * 문자열을 자르면 지역시간과 날짜가 어긋난다(한국이면 UTC 15시 이후가 다음 날이다).
 * 그래서 fmtServerDateTime 과 같은 자로 파싱한 뒤 월·일만 남긴다.
 */
export function fmtServerDateShort(s) {
  return formatV2Timestamp(s, 'monthDay');
}

/**
 * 배정 한 건의 채점 집계 — 2026-09-06 회신 §1.
 *
 * `graded` 를 정답 수로 읽으면 화면이 거짓 숫자를 낸다. 계약 §2-9 가 그 값을 「채점 기록이
 * 존재하는 문항 수(null 포함)」로 못박았고, 회신이 그 안을 셋으로 갈라 줬다:
 * correct + wrong + unanswered = graded.
 *
 * 숫자가 아닌 값은 null 로 돌린다 — 0 으로 메우면 「아직 안 온 값」과 「정말 0」이 같은 얼굴이 된다.
 */
export function assignmentCounts(row) {
  const n = (v) => {
    if (v == null || v === '') return null;
    const x = Number(v);
    return Number.isFinite(x) ? x : null;
  };
  return {
    correct: n(row?.correct_count),
    wrong: n(row?.wrong_count),
    unanswered: n(row?.unanswered_count),
    graded: n(row?.graded),
    total: n(row?.total_items),
  };
}

/**
 * 정답률 — **마감된 배정에만 뜻이 있다.** 진행 중에는 채점이 늘 때마다 값이 출렁여, 보는 사람이
 * 「이 학생 30%」를 실력으로 읽는다. 레거시(ExamsByPresetModal:154)도 마감분에만 그렸다.
 *
 * 분모는 **채점된 문항(정답+오답)** 이다 — 2026-09-16 오너 결정으로 `total_items` 에서 바꿨다.
 * 회신 권고는 `total_items`(미채점=오답 취급)였지만 결정권은 화면에 있다고 못박혀 있고(회신 §1
 * 「정답률 분모: 둘 다 있으니 화면이 정합니다」), 계약이 `unanswered_count` 를 지운 대신 남겨 둔
 * 이유도 이 선택을 위해서다(같은 회신 §3-3 「정답률 분모 선택용」).
 * 미채점은 **틀린 것이 아니라 아직 모르는 것**이라, 오답과 같은 칸에 넣으면 채점을 덜 한 시험지가
 * 못 맞힌 시험지와 같은 얼굴이 된다. 구 화면도 같은 자다 — `Datapage.js:103` 이
 * `correct / (correct + wrong)` 로 적는다.
 *
 * 그래서 분모에서 두 가지가 함께 빠진다 — 계약이 가른 미채점의 두 갈래다:
 *   ① 채점 기록은 있는데 결과가 빈 것 = `unanswered_count`
 *   ② 채점 기록 자체가 없는 것 = `total_items - graded`
 * 둘 다 「사람이 아직 안 본 문항」이다. 정답+오답만 더하면 둘 다 자연히 빠진다.
 *
 * `wrong` 이 안 오면 분모를 못 만들어 `null` 이다 — 0 으로 메우면 「안 온 값」이 「정말 0」과
 * 같은 얼굴이 되어 미채점 시험지가 100% 로 선다. 정답·오답 칸도 같은 판정을 쓴다(AssignPanel:1109).
 * @returns 0~100 정수, 또는 잴 수 없으면 null
 */
export function accuracyOf(row) {
  if (!isAssignmentClosed(row)) return null;
  const { correct, wrong } = assignmentCounts(row);
  if (correct == null || wrong == null) return null;
  const scored = correct + wrong;
  if (scored <= 0) return null;
  return Math.round((correct / scored) * 100);
}

/**
 * 마감 판정 — is_closed 또는 status===200(계약 §1-2 A2). 채점을 다 해도 status 는 50/100 에 머문다.
 * 형제 화면(AssignmentPanel:isClosedOf)과 같은 자를 쓴다.
 */
export function isAssignmentClosed(row) {
  return row?.is_closed === true || row?.status === 200;
}

/**
 * 정답률의 색 — 레거시가 쓰던 두 문턱(80·60)을 그대로 옮긴다(ExamsByPresetModal:107·192).
 * 같은 수치가 화면마다 다른 색이면 한 값이 자리마다 다른 뜻으로 읽힌다.
 */
export function accuracyTone(rate) {
  if (rate == null) return 'text-gray-400';
  if (rate >= 80) return 'text-green-600';
  if (rate >= 60) return 'text-yellow-600';
  return 'text-red-600';
}

/**
 * 정답률이 무엇을 나눈 값인지 — 숫자에 덧붙이는 한 문장(title).
 *
 * 화면은 「정답 24 · 오답 5 · 총 38」과 「83%」를 나란히 적는다. 분모가 38 이 아니라 29 라는 것을
 * 보는 사람이 모르면 24÷38 을 눈으로 셈해 보고 숫자를 의심한다. 칸을 새로 만들지 않고 이 문장으로
 * 말한다 — 보이던 표시를 지우지 않으면서 셈을 드러내는 가장 싼 자리다.
 *
 * 두 화면(출제 이력·학습 배정 피드)이 **같은 글자**를 쓴다. 같은 값이 자리마다 다른 문장으로
 * 설명되면 그것대로 두 숫자가 된다.
 * @returns {string|undefined} 잴 수 없으면 undefined — title 속성에 그대로 넘겨 「없으면 안 붙는다」
 */
export function accuracyBasisText(correct, wrong, total) {
  if (correct == null || wrong == null) return undefined;
  const scored = correct + wrong;
  if (scored <= 0) return undefined;
  const head = `정답 ${correct} ÷ 채점한 ${scored}문항`;
  const skipped = total == null ? 0 : total - scored;
  return skipped > 0 ? `${head} — 미채점 ${skipped}문항은 빼고 셉니다` : head;
}

/**
 * 정답률 자리에 숫자 대신 「—」가 선 이유 — 이것도 title 한 문장이다.
 *
 * 분모를 정답+오답으로 바꾸면서 「—」의 이유가 **둘**로 늘었다. 마감 전이라 안 재는 것과,
 * 마감은 했는데 채점한 문항이 하나도 없어 못 재는 것은 다른 일이다. 둘에 같은 문장을 붙이면
 * 마감된 시험지를 보면서 「채점을 완료해야 나옵니다」를 읽게 되어 화면이 거짓을 말한다.
 */
export function accuracyEmptyText(row) {
  if (!isAssignmentClosed(row)) return '채점을 완료해야 정답률이 나옵니다';
  const { correct, wrong } = assignmentCounts(row);
  if (correct == null || wrong == null) return '채점 집계가 오지 않아 정답률을 낼 수 없습니다';
  return '채점한 문항이 없어 정답률을 낼 수 없습니다';
}

/**
 * 배정의 난이도 — 서버가 **평균 × 100 을 반올림한 정수**로 준다(2026-09-06 회신 §2 4-7. `188` = 1.88).
 * 값의 뜻은 「배정 시점 문항 난이도(step)의 평균」이고 시험지 자체의 값이 아니다(같은 회신 4-6).
 *
 * 100 으로 나눠 **소수 둘째 자리까지** 적는다. 새로 정한 자가 아니라 이 저장소가 이미 여섯 곳에서
 * 쓰는 식이다(`AssignmentPanel:560` · HubAssignmentCard · ReportModal · PresetTable · Datapage · useTests).
 * 같은 배정이 채점 창에서는 `1.88`, 목록에서는 `188` 로 보이면 둘 다 못 믿는다.
 *
 * **0 이하와 숫자 아님은 `null`** 이다. 없는 값을 `0.00` 으로 적으면 가장 쉬운 시험처럼 보이는데,
 * 난이도 평균은 문항이 하나라도 있으면 100 이상이라 0 은 「값이 없다」는 뜻이다(구 화면도 같은 판정 —
 * `ExamList.js:845` 가 falsy 를 `-` 로 그린다).
 * @returns {string|null} `'1.88'` 같은 문자열, 또는 잴 수 없으면 null
 */
export function assignmentDifficulty(row) {
  const n = Number(row?.difficulty);
  return Number.isFinite(n) && n > 0 ? (n / 100).toFixed(2) : null;
}

/**
 * 배포 목록의 합계 — **마감된 배정만** 센다(레거시 ExamsByPresetModal:83 과 같은 규칙).
 * 채점 중인 배정을 섞으면 아직 안 매긴 문항이 오답처럼 깔려 정답률이 실제보다 낮게 나온다.
 *
 * 분모는 행별 정답률과 **같은 자**여야 한다 — 띠가 `total_items`, 표가 정답+오답이면 같은 시험지를
 * 두고 두 숫자가 갈리고 그러면 둘 다 못 믿는다. 그래서 여기도 정답+오답이다(`accuracyOf` 주석 참조).
 * `total` 은 계속 돌려준다 — 화면의 「총문제」 칸은 배정된 문항 수 그대로이고 분모가 아니다.
 * @returns {{closedCount, correct, wrong, total, rate}} — 마감분이 없으면 closedCount 0
 */
export function assignmentsSummary(rows) {
  const list = Array.isArray(rows) ? rows.filter(isAssignmentClosed) : [];
  let correct = 0; let wrong = 0; let total = 0;
  list.forEach((r) => {
    const c = assignmentCounts(r);
    correct += c.correct ?? 0;
    wrong += c.wrong ?? 0;
    total += c.total ?? 0;
  });
  const scored = correct + wrong;
  return {
    closedCount: list.length,
    correct,
    wrong,
    total,
    rate: scored > 0 ? Math.round((correct / scored) * 100) : null,
  };
}

/** 학교·학년 — 둘 다 선택값이라 있는 것만 잇는다(2026-09-06 회신 §1, 삭제 학생은 null) */
export function studentSchoolText(row) {
  const parts = [row?.school_name, row?.grade]
    .map((v) => (v == null ? '' : String(v).trim()))
    .filter((v) => v !== '');
  return parts.join(' ');
}

/** 담당 교사·강의 — 같은 규칙. 교사는 **이름 문자열**이고 uuid 는 따로 온다(회신 §4-2) */
export function studentClassText(row) {
  const parts = [row?.in_charge_teacher, row?.lecture]
    .map((v) => (v == null ? '' : String(v).trim()))
    .filter((v) => v !== '');
  return parts.join(' · ');
}
